import React, { useMemo } from "react";

import clsx from "clsx";

import { BaseCard } from "./BaseCard";
import PieChart from "../Graphs/PieChart";
import LoadingWheel from "../Shared/LoadingWheel";
import { useNurtureCampaignStats } from "../../data/nurtureCampaigns/useNurtureCampaignStats";
import { INurtureCampaignRuleStatistic } from "../../models/nurtureCampaign/NurtureCampaignStatistics";
import { useNurtureCampaign } from "../../data/nurtureCampaigns/useNurtureCampaign";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { set } from "react-hook-form";
import { CampaignRecipientsModal } from "./CampaignRecipientsModal";

/**
 * Accumulates stats from an array of campaign statistic objects
 */
function accumulateStats(statsArray: Omit<INurtureCampaignRuleStatistic, "nurtureRuleId">[]) {
  return statsArray.reduce((acc, current) => ({
    totalCount: acc.totalCount + (current.totalCount || 0),
    deliveredCount: acc.deliveredCount + (current.deliveredCount || 0),
    openCount: acc.openCount + (current.openCount || 0),
    uniqueOpenCount: acc.uniqueOpenCount + (current.uniqueOpenCount || 0),
    clickCount: acc.clickCount + (current.clickCount || 0),
    uniqueClickCount: acc.uniqueClickCount + (current.uniqueClickCount || 0),
  }), {
    totalCount: 0,
    deliveredCount: 0,
    openCount: 0,
    uniqueOpenCount: 0,
    clickCount: 0,
    uniqueClickCount: 0,
  });
};

interface StatCardProps {
  title: string;
  value: string | number;
  className?: string;
}

function StatCard({ title, value, className }: StatCardProps) {
  return (
    <div className={clsx("flex flex-col items-center justify-center px-4", className)}>
      <dt className="font-medium text-gray-700 truncate">
        {title}
      </dt>
      <dd className="mt-1 text-3xl font-semibold text-etpink-600">
        {value}
      </dd>
    </div>
  )
}

interface CampaignStatsProps {
  agencyId: string;
  campaignId: string;
}

export function CampaignStatsDisplay({ agencyId, campaignId }: CampaignStatsProps) {
  const { stats } = useNurtureCampaignStats(agencyId, campaignId);
  const { nurtureCampaign } = useNurtureCampaign(agencyId, campaignId);
  const [selectedRuleId, setSelectedRuleId] = React.useState<string>();
  const [recipientsOpen, setRecipientsOpen] = React.useState(false);

  const campaignTotals = useMemo(() => [
    accumulateStats(stats.data?.data?.ruleStatistics || [])
  ], [stats.data]);

  const getChartData = (data: Omit<INurtureCampaignRuleStatistic, "nurtureRuleId">) => ({
    labels: ["Total Sent", "Total Delivered", "Total Opens", "Total Clicks", "Total Unique Opens", "Total Unique Clicks"],
    datasets: [
      {
        data: [
          data.totalCount,
          data.deliveredCount,
          data.openCount,
          data.clickCount,
          data.uniqueOpenCount,
          data.uniqueClickCount,
        ],
      },
    ],
  });

  const chartData = getChartData(campaignTotals[0]);

  return (
    <>
      <CampaignRecipientsModal
        open={recipientsOpen}
        setOpen={setRecipientsOpen}
        nurtureRuleId={selectedRuleId!}
      />
      <BaseCard title="Overall Campaign Stats">
        {stats.isLoading ?
          <div className="flex py-10 justify-center">
            <LoadingWheel width="w-8" height="h-8" />
          </div>
          :
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-x gap-6">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <StatCard
                title="Total Sent"
                value={campaignTotals[0].totalCount}
                className="border-b lg:border-r"
              />
              <StatCard
                title="Total Delivered"
                value={campaignTotals[0].deliveredCount}
                className="border-b"
              />
              <StatCard
                title="Total Opens"
                value={campaignTotals[0].openCount}
                className="border-b lg:border-r"
              />
              <StatCard
                title="Total Clicks"
                value={campaignTotals[0].clickCount}
                className="border-b"
              />
              <StatCard
                title="Total Unique Opens"
                value={campaignTotals[0].uniqueOpenCount}
                className="border-b lg:border-b-0 lg:border-r"
              />
              <StatCard
                title="Total Unique Clicks"
                value={campaignTotals[0].uniqueClickCount}
                className="border-b lg:border-b-0"
              />
            </div>
            <div>
              <PieChart
                data={chartData}
                height={250}
                width="100%"
              />
            </div>
          </div>
        }
      </BaseCard>
      {
        stats.isLoading || nurtureCampaign.isLoading ? <></>
          :
          stats.data?.data.ruleStatistics.map((rule, index) => {
            const ruleDetails = nurtureCampaign.data?.data.rules.find(r => r.id === rule.nurtureRuleId);

            return <BaseCard
              key={rule.nurtureRuleId}
              title={`Statistics for Email ${index + 1}: ${ruleDetails?.name}`}
              rightAdornment={
                // @ts-ignore
                <ButtonInput
                  label='View Recipients'
                  onClick={() => {
                    setSelectedRuleId(rule.nurtureRuleId);
                    setRecipientsOpen(true);
                  }}
                />
              }
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-x gap-6">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <StatCard
                    title="Sent"
                    value={rule.totalCount}
                    className="border-b lg:border-r"
                  />
                  <StatCard
                    title="Delivered"
                    value={rule.deliveredCount}
                    className="border-b"
                  />
                  <StatCard
                    title="Opens"
                    value={rule.openCount}
                    className="border-b lg:border-r"
                  />
                  <StatCard
                    title="Clicks"
                    value={rule.clickCount}
                    className="border-b"
                  />
                  <StatCard
                    title="Unique Opens"
                    value={rule.uniqueOpenCount}
                    className="border-b lg:border-b-0 lg:border-r"
                  />
                  <StatCard
                    title="Unique Clicks"
                    value={rule.uniqueClickCount}
                    className="border-b lg:border-b-0"
                  />
                </div>
                <div>
                  <PieChart
                    data={getChartData(rule)}
                    height={250}
                    width="100%"
                  />
                </div>
              </div>
            </BaseCard>
          }
          )
      }
    </>
  )

}